import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Card,
  Empty,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Radio,
  Result,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import {
  DeleteOutlined,
  EditOutlined,
  IdcardOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useMediaQuery } from 'react-responsive'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import api, { getApiErrorMessage } from '../utils/api'
import {
  createClassTicketTemplate,
  deleteClassTicketTemplate,
  listClassTicketTemplates,
  updateClassTicketTemplate
} from '../api/classTicket'
import Hint from '../components/common/Hint'
import ResponsiveDatePicker from '../components/common/ResponsiveDatePicker'
import { inTz, todayKey, wallTime } from '../utils/timezone'
import { describeValidity } from '../utils/classTicketExpiry'

const { Title, Text } = Typography

const PageWrapper = styled.div`
  min-height: calc(100vh - 120px);
  padding: 24px;
  background: #f5f5f5;

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const HeaderCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid #eef0f5;
  box-shadow: 0 6px 18px rgba(15, 37, 98, 0.05);

  .ant-card-body {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
    justify-content: space-between;
  }

  @media (max-width: 768px) {
    .ant-card-body {
      flex-direction: column;
      align-items: stretch;
      justify-content: center;
      text-align: center;
    }

    .ant-card-body > .ant-space {
      justify-content: center;
      width: 100%;
    }
  }
`

const TableCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid #eef0f5;
  box-shadow: 0 6px 18px rgba(15, 37, 98, 0.05);
`

const ToolBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`

const DesktopTable = styled(Table)`
  @media (max-width: 768px) {
    display: none;
  }
`

const MobileList = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
`

const MobileItemCard = styled.div`
  background: #fff;
  border: 1px solid #eef0f5;
  border-radius: 12px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const MobileTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`

const MobileTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #2c2531;
  flex: 1;
  word-break: break-all;
`

const MobileMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 13px;
  color: #6b7280;

  span b {
    color: #1d1d1f;
    font-weight: 600;
    margin-left: 2px;
  }
`

const MobileActionRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;

  .ant-btn {
    flex: 1;
  }
`

/**
 * 有效期限的模式切換。
 * 用 Radio.Button 而不是 Select —— 兩個選項而已，而且觸控裝置上的下拉浮層
 * 很難滑（專案慣例見 CLAUDE.md），這裡直接攤平成兩顆按鈕，手機上各佔一半。
 */
const ValidityModeRadio = styled(Radio.Group)`
  @media (max-width: 768px) {
    display: flex;
    width: 100%;

    .ant-radio-button-wrapper {
      flex: 1;
      text-align: center;
    }
  }
`

const ClassTicketSettingPage = () => {
  const { user } = useAuth()
  const { hasFeature, permissionsLoaded } = useMembership()
  const businessId = user?.businessId

  const [loading, setLoading] = useState(false)
  const [keyword, setKeyword] = useState('')
  const [includeInactive, setIncludeInactive] = useState(false)
  const [templates, setTemplates] = useState([])

  const [services, setServices] = useState([])
  const [servicesLoading, setServicesLoading] = useState(false)

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)
  const isMobile = useMediaQuery({ maxWidth: 768 })
  // 有效期限的兩種模式要即時換欄位，所以得盯著這個值
  const validityMode = Form.useWatch('validityMode', form) || 'days'
  const validUntilValue = Form.useWatch('validUntil', form)

  const featureEnabled = hasFeature('classTicket')

  const fetchServices = useCallback(async () => {
    if (!businessId) return
    setServicesLoading(true)
    try {
      const res = await api.get(`/api/business_B/${businessId}/products`)
      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
          ? res.data.data
          : []
      setServices(
        list.map((p) => ({
          _id: p._id,
          name: p.name,
          basePrice: p.basePrice ?? p.price ?? 0
        }))
      )
    } catch (err) {
      console.warn('fetch services failed', err)
      setServices([])
    } finally {
      setServicesLoading(false)
    }
  }, [businessId])

  const fetchTemplates = useCallback(async () => {
    if (!businessId) return
    setLoading(true)
    try {
      const res = await listClassTicketTemplates(businessId, {
        keyword,
        includeInactive: includeInactive ? '1' : '0'
      })
      setTemplates(res?.data?.data || [])
    } catch (err) {
      message.error(getApiErrorMessage(err, '取得堂票範本失敗'))
    } finally {
      setLoading(false)
    }
  }, [businessId, keyword, includeInactive])

  useEffect(() => {
    fetchServices()
  }, [fetchServices])

  useEffect(() => {
    if (featureEnabled) fetchTemplates()
  }, [fetchTemplates, featureEnabled])

  const serviceMap = useMemo(() => {
    const m = new Map()
    services.forEach((s) => m.set(String(s._id), s))
    return m
  }, [services])

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({
      isActive: true,
      totalCount: 10,
      validityMode: 'days',
      validityDays: 90,
      validUntil: null,
      price: 0,
      applicableServiceIds: [],
      selfCollected: false,
      excludeFromSalesCommission: false,
      showOnBookingPage: false
    })
    setModalOpen(true)
  }

  const openEdit = (record) => {
    setEditing(record)
    const mode = record.validityMode === 'date' ? 'date' : 'days'
    form.setFieldsValue({
      name: record.name,
      totalCount: record.totalCount,
      validityMode: mode,
      // 天數欄位留著預設值，店家切回天數制時不會是空的
      validityDays: record.validityDays || 90,
      /**
       * 後端另外給的 validUntilDate 已經是店家時區的那一天（牆上日期）；
       * 沒有時才退回 validUntil（絕對時刻）自己換算。
       */
      validUntil: record.validUntilDate
        ? wallTime(record.validUntilDate, 'YYYY-MM-DD')
        : record.validUntil
          ? inTz(record.validUntil)
          : null,
      price: record.price || 0,
      applicableServiceIds: record.applicableServiceIds || [],
      description: record.description || '',
      isActive: record.isActive !== false,
      selfCollected: record.selfCollected === true,
      excludeFromSalesCommission: record.excludeFromSalesCommission === true,
      showOnBookingPage: record.showOnBookingPage === true
    })
    setModalOpen(true)
  }

  const submitForm = async () => {
    try {
      const values = await form.validateFields()
      setSubmitting(true)
      /**
       * 有效期限只送當前模式用到的那一個欄位。
       * 直接 format 欄位上顯示的那一天送純日期字串，由後端補上店家時區當天的
       * 23:59:59 —— 中間多繞 ISO 時刻的話，店家在國外操作就會差一天。
       *
       * 這裡不能再經過 fromPicker()：新挑的值是瀏覽器牆上時間，但編輯既有範本時
       * openEdit 放進表單的是 wallTime()/inTz() 產生的「店家時區」值，
       * 對它做 keepLocalTime 換算會把日期往前推一天（裝置時區≠店家時區時）。
       * 兩種來源的 format('YYYY-MM-DD') 都已經是欄位上看到的那一天。
       */
      const payload = { ...values }
      if (values.validityMode === 'date') {
        payload.validUntil = values.validUntil?.format('YYYY-MM-DD')
        delete payload.validityDays
      } else {
        payload.validUntil = null
      }
      if (editing) {
        await updateClassTicketTemplate(businessId, editing._id, payload)
        message.success('範本已更新')
      } else {
        await createClassTicketTemplate(businessId, payload)
        message.success('範本已新增')
      }
      setModalOpen(false)
      fetchTemplates()
    } catch (err) {
      if (err && err.errorFields) return // 表單驗證失敗
      message.error(getApiErrorMessage(err, '儲存失敗'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (record) => {
    try {
      await deleteClassTicketTemplate(businessId, record._id)
      message.success('已刪除')
      fetchTemplates()
    } catch (err) {
      message.error(getApiErrorMessage(err, '刪除失敗'))
    }
  }

  if (!permissionsLoaded) {
    return (
      <PageWrapper>
        <Spin />
      </PageWrapper>
    )
  }
  if (!featureEnabled) {
    return (
      <PageWrapper>
        <Result
          status='warning'
          title='您的方案尚未開通堂票模組'
          subTitle='請升級至高階方案以使用堂票功能。'
        />
      </PageWrapper>
    )
  }

  const columns = [
    {
      title: '堂票名稱',
      dataIndex: 'name',
      key: 'name',
      render: (v) => <Text strong>{v}</Text>
    },
    {
      title: '可使用張數',
      dataIndex: 'totalCount',
      key: 'totalCount',
      width: 110,
      align: 'right',
      render: (v) => `${v} 張`
    },
    {
      title: '有效期限',
      dataIndex: 'validityDays',
      key: 'validityDays',
      width: 150,
      render: (_, record) => describeValidity(record)
    },
    {
      title: '售價',
      dataIndex: 'price',
      key: 'price',
      width: 100,
      align: 'right',
      render: (v) => (v ? `$ ${Number(v).toLocaleString()}` : '—')
    },
    {
      title: '適用服務',
      dataIndex: 'applicableServiceIds',
      key: 'applicableServiceIds',
      render: (ids) => {
        if (!ids || ids.length === 0) return <Tag>不限</Tag>
        return (
          <Space size={[4, 4]} wrap>
            {ids.slice(0, 3).map((id) => (
              <Tag key={String(id)}>
                {serviceMap.get(String(id))?.name || '已刪除服務'}
              </Tag>
            ))}
            {ids.length > 3 && <Tag>+{ids.length - 3}</Tag>}
          </Space>
        )
      }
    },
    {
      title: '狀態',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 90,
      render: (v, record) => (
        <Space size={[4, 4]} wrap>
          {v ? <Tag color='green'>啟用</Tag> : <Tag>停用</Tag>}
          {record.selfCollected && <Tag color='purple'>員工自收</Tag>}
          {record.excludeFromSalesCommission && <Tag>不計業績</Tag>}
          {record.showOnBookingPage && <Tag color='magenta'>預約頁推薦</Tag>}
        </Space>
      )
    },
    {
      title: '操作',
      key: 'actions',
      width: 90,
      fixed: 'right',
      align: 'center',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title='編輯'>
            <Button
              type='text'
              size='small'
              icon={<EditOutlined />}
              onClick={() => openEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title='確定刪除此範本？'
            description='已發放的堂票不受影響。'
            okText='刪除'
            okType='danger'
            cancelText='取消'
            onConfirm={() => handleDelete(record)}
          >
            <Tooltip title='刪除'>
              <Button
                type='text'
                size='small'
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <PageWrapper>
      <ContentWrapper>
        <HeaderCard>
          <Space size='middle' align='center'>
            <IdcardOutlined style={{ fontSize: 22, color: '#d5728a' }} />
            <Title level={4} style={{ margin: 0 }}>
              堂票設定
            </Title>
          </Space>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchTemplates}>
              重新整理
            </Button>
            <Button
              type='primary'
              icon={<PlusOutlined />}
              onClick={openCreate}
            >
              新增堂票
            </Button>
          </Space>
        </HeaderCard>

        <TableCard>
          <ToolBar>
            <Input
              allowClear
              placeholder='輸入堂票名稱搜尋'
              prefix={<SearchOutlined />}
              style={{ width: 280 }}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onPressEnter={fetchTemplates}
            />
            <Space>
              <Switch
                checked={includeInactive}
                onChange={setIncludeInactive}
              />
              <Text type='secondary'>顯示已停用</Text>
            </Space>
          </ToolBar>

          <DesktopTable
            rowKey='_id'
            columns={columns}
            dataSource={templates}
            loading={loading}
            scroll={{ x: 800 }}
            locale={{
              emptyText: <Empty description='尚未建立堂票範本' />
            }}
            pagination={{ pageSize: 20, showSizeChanger: false }}
          />

          <MobileList>
            {loading ? (
              <Spin />
            ) : templates.length === 0 ? (
              <Empty description='尚未建立堂票範本' />
            ) : (
              templates.map((record) => (
                <MobileItemCard key={record._id}>
                  <MobileTitleRow>
                    <MobileTitle>{record.name}</MobileTitle>
                    <Space size={4}>
                      {record.selfCollected && <Tag color='purple'>員工自收</Tag>}
                      {record.excludeFromSalesCommission && <Tag>不計業績</Tag>}
                      {record.showOnBookingPage && <Tag color='magenta'>預約頁推薦</Tag>}
                      {record.isActive !== false ? (
                        <Tag color='green'>啟用</Tag>
                      ) : (
                        <Tag>停用</Tag>
                      )}
                    </Space>
                  </MobileTitleRow>
                  <MobileMetaRow>
                    <span>
                      張數<b>{record.totalCount}</b>
                    </span>
                    <span>
                      有效<b>{describeValidity(record, { short: true })}</b>
                    </span>
                    <span>
                      售價
                      <b>
                        {record.price
                          ? `$${Number(record.price).toLocaleString()}`
                          : '—'}
                      </b>
                    </span>
                  </MobileMetaRow>
                  <MobileMetaRow>
                    <span>
                      適用：
                      {!record.applicableServiceIds ||
                      record.applicableServiceIds.length === 0
                        ? '不限'
                        : `${record.applicableServiceIds
                            .slice(0, 2)
                            .map(
                              (id) =>
                                serviceMap.get(String(id))?.name || '已刪除'
                            )
                            .join('、')}${
                            record.applicableServiceIds.length > 2
                              ? ` 等 ${record.applicableServiceIds.length} 項`
                              : ''
                          }`}
                    </span>
                  </MobileMetaRow>
                  <MobileActionRow>
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => openEdit(record)}
                    >
                      編輯
                    </Button>
                    <Popconfirm
                      title='確定刪除此範本？'
                      description='已發放的堂票不受影響。'
                      okText='刪除'
                      okType='danger'
                      cancelText='取消'
                      onConfirm={() => handleDelete(record)}
                    >
                      <Button danger icon={<DeleteOutlined />}>
                        刪除
                      </Button>
                    </Popconfirm>
                  </MobileActionRow>
                </MobileItemCard>
              ))
            )}
          </MobileList>
        </TableCard>
      </ContentWrapper>

      <Modal
        open={modalOpen}
        title={editing ? '編輯堂票範本' : '新增堂票範本'}
        okText='儲存'
        cancelText='取消'
        onOk={submitForm}
        onCancel={() => setModalOpen(false)}
        confirmLoading={submitting}
        destroyOnClose
        width={640}
      >
        <Form form={form} layout='vertical'>
          <Form.Item
            label='堂票名稱'
            name='name'
            rules={[{ required: true, message: '請輸入名稱' }]}
          >
            <Input placeholder='例：新客套組2998' maxLength={60} />
          </Form.Item>
          <Space size='middle' style={{ display: 'flex' }} wrap>
            <Form.Item
              label='可使用張數'
              name='totalCount'
              rules={[{ required: true, message: '請輸入張數' }]}
              style={{ minWidth: 180 }}
            >
              <InputNumber min={1} max={9999} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label='售價' name='price' style={{ minWidth: 160 }}>
              <InputNumber
                min={0}
                step={100}
                style={{ width: '100%' }}
                formatter={(v) =>
                  v ? `$ ${Number(v).toLocaleString()}` : ''
                }
                parser={(v) => (v ? v.replace(/\D/g, '') : 0)}
              />
            </Form.Item>
          </Space>

          <Form.Item label='有效期限' name='validityMode' required>
            <ValidityModeRadio buttonStyle='solid'>
              <Radio.Button value='days'>領取後 N 天</Radio.Button>
              <Radio.Button value='date'>指定到期日</Radio.Button>
            </ValidityModeRadio>
          </Form.Item>

          {validityMode === 'date' ? (
            <Form.Item
              label='到期日期'
              name='validUntil'
              rules={[{ required: true, message: '請選擇到期日期' }]}
              extra='不論哪天領取，都在這一天 23:59 到期。適合年度活動票、跨年套組這種大家同一天到期的堂票。'
            >
              {/* 手機／平板會換成彈出式的年月日抽屜（翻日曆在觸控上太慢） */}
              <ResponsiveDatePicker
                style={{ width: '100%' }}
                format='YYYY-MM-DD'
                placeholder='選擇到期日期'
                size={isMobile ? 'large' : 'middle'}
                // 桌機的日曆輸入框在手機上用不到，這裡只對桌機有意義
                inputReadOnly={isMobile}
                // 今天以前不給選（今天可選＝當天到期）
                disabledDate={(current) =>
                  current && current.format('YYYY-MM-DD') < todayKey()
                }
                allowClear={false}
              />
            </Form.Item>
          ) : (
            <Form.Item
              label='有效天數（領取後 N 天）'
              name='validityDays'
              rules={[{ required: true, message: '請輸入有效天數' }]}
              style={{ maxWidth: 260 }}
            >
              <InputNumber
                min={1}
                max={3650}
                style={{ width: '100%' }}
                addonAfter='天'
              />
            </Form.Item>
          )}

          {validityMode === 'date' &&
            validUntilValue &&
            validUntilValue.format('YYYY-MM-DD') < todayKey() && (
              <Hint tone='warning' style={{ marginBottom: 16 }}>
                這個到期日已經過了，之後發放或結帳售出的堂票會立刻是過期狀態。請改日期或改用「領取後 N 天」。
              </Hint>
            )}
          <Form.Item
            label='適用服務（不選代表不限服務）'
            name='applicableServiceIds'
          >
            <Select
              mode='multiple'
              placeholder='選擇此堂票可折抵的服務項目'
              loading={servicesLoading}
              optionFilterProp='label'
              options={services.map((s) => ({
                label: s.name,
                value: s._id
              }))}
              maxTagCount='responsive'
              allowClear
            />
          </Form.Item>
          <Form.Item label='範本說明' name='description'>
            <Input.TextArea
              rows={3}
              maxLength={500}
              placeholder='選填，會顯示給客戶看'
            />
          </Form.Item>
          <Form.Item
            label='員工自收堂票'
            name='selfCollected'
            valuePropName='checked'
            extra='售價由做的人直接收全額（例：由美容師自己收款的療程套票），不算店家堂票營收、也不算堂票銷售抽成。之後每核銷一堂，依該服務抽成規則的「員工自收」比例繳回店家。'
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label='不計入堂票銷售業績／抽成'
            name='excludeFromSalesCommission'
            valuePropName='checked'
            extra='售價照樣算店家營收，但不算任何人的堂票銷售業績，也不抽堂票銷售抽成。適合只給執行技術費的套票（技術費在「服務項目抽成設定」的每次固定抽成設定）。'
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label='在線上預約頁推薦'
            name='showOnBookingPage'
            valuePropName='checked'
            extra='打開後，客人在美感預約頁選到這張堂票適用的服務時，會看到「買堂票更划算」的推薦（顯示名稱、堂數、售價、每堂均價與範本說明）。客人可勾選「想了解」，店家會在預約備註看到。堂票仍由店家到店販售。'
          >
            <Switch />
          </Form.Item>
          <Form.Item label='啟用' name='isActive' valuePropName='checked'>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </PageWrapper>
  )
}

export default ClassTicketSettingPage
